import React, { useState } from 'react';

import {
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	TextField,
	Box,
	FormHelperText,
	Tooltip,
} from '@mui/material';

import {
	IQuestionnaireFormWidgetProps,
	IQuestionOption,
} from 'src/features/questionnaire/domain/types';

const RadioButtonWithTextFieldWidget: React.FC<
	IQuestionnaireFormWidgetProps
> = ({
	question,
	onValueChange,
	isDisabled,
	error,
	helperText = 'This field is invalid',
	compactMode = false,
}) => {
	const radioValue =
		question.submitted_data?.value_single_select?.value.toString() ?? '';

	const textFieldValue =
		question.submitted_data?.value_single_select?.text ?? '';

	const [selectedRadioValue, setSelectedRadioValue] = useState(radioValue);
	const [textFieldInput, setTextFieldInput] = useState(
		selectedRadioValue === radioValue ? textFieldValue : '',
	);

	const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSelectedRadioValue(value);
		setTextFieldInput('');

		onValueChange(
			question,
			{ value: value, text: '' },
			'value_single_select',
		);
	};

	const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newText = e.target.value;
		setTextFieldInput(newText);
		onValueChange(
			question,
			{ value: selectedRadioValue, text: newText },
			'value_single_select',
		);
	};

	const getToolTipTitleForTextField = (
		option: IQuestionOption,
		isOptionSelected: boolean,
	) => {
		return (
			<span style={{ whiteSpace: 'pre-line' }}>
				Label: {option.label}
				{isOptionSelected && textFieldInput
					? `\n\nAns: ${textFieldInput}`
					: ''}
			</span>
		);
	};

	return (
		<FormControl
			component='fieldset'
			required={question.is_required}
			disabled={isDisabled}
			sx={{ width: '100%', textAlign: 'left' }}
			error={error}
		>
			<FormLabel component='legend'>{question.text}</FormLabel>
			<RadioGroup defaultValue={radioValue} onChange={handleRadioChange}>
				{question.options.map((option) => {
					const isOptionSelected =
						selectedRadioValue === option.id.toString();

					return (
						<Box
							key={option.id}
							sx={{
								display: 'flex',
								flexDirection: compactMode
									? 'column'
									: { xs: 'column', sm: 'row' },
								alignItems: compactMode
									? 'flex-start'
									: { xs: 'flex-start', sm: 'center' },
								mt: 0.5,
								width: '100%',
								gap: compactMode ? 1 : { xs: 1, sm: 0 },
							}}
						>
							<FormControlLabel
								value={option.id.toString()}
								control={<Radio />}
								label={option.value}
								sx={{ marginLeft: 0 }}
							/>
							{option.allow_text && (
								<Tooltip
									title={getToolTipTitleForTextField(
										option,
										isOptionSelected,
									)}
									arrow
									placement='left-start'
								>
									<TextField
										sx={{
											ml: 2,
											width: '100%',
											'& input': {
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
											},
										}}
										placeholder={option.label || ''}
										value={
											isOptionSelected
												? textFieldInput
												: ''
										}
										disabled={
											!isOptionSelected || isDisabled
										}
										onChange={handleTextFieldChange}
										error={isOptionSelected && error}
									/>
								</Tooltip>
							)}
						</Box>
					);
				})}
			</RadioGroup>
			{error && <FormHelperText>{helperText}</FormHelperText>}
		</FormControl>
	);
};

export default RadioButtonWithTextFieldWidget;
